import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { DateTime } from 'luxon';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import Chip from '@material-ui/core/Chip';
import Divider from '@material-ui/core/Divider';

import { container } from 'app/styles/new-layout';

const useStyles = makeStyles(theme => ({
    container: {
        ...container(theme),
        padding: theme.spacing(3),
        maxWidth: 800
    },
    section: {
        marginBottom: theme.spacing(3)
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    marginLeft: {
        marginLeft: theme.spacing(3)
    },
    error: {
        color: theme.palette.error.main,
        fontSize: 12,
        display: 'block'
    }
}));

export default function ElectionReview(props) {

    const {
        election,
        groups,
        isError,
        getErrorMessage,
        electionNameWithDate,
        newElection
    } = props;

    const classes = useStyles();

    const getGroupTitle = (group_id, defaultValue = "All Users") => {
        if (group_id !== 0) {
            return groups.find(group => group.id === group_id).title;
        }
        else return defaultValue;
    };

    const getValue = value => {
        if (!value || value === null || value === "") return 'n/a';
        return value;
    }

    const getDate = value => {
        if (DateTime.isDateTime(value)) return value.toLocaleString(DateTime.DATETIME_FULL);
        return 'n/a';
    }

    const getNumber = (value, zeroValue = 'No Limit') => {
        if (!value || value === null || value == 0) return zeroValue;
        return value;
    }

    return (
        <div className={classes.container}>
            <Alert severity="info" className={classes.section}>
                Please review your election details then click <strong>FINISH</strong> to submit your election.
            </Alert>
            <div className={classes.section}>
                <Typography variant="h2" component="h4">
                    {election.name}
                    {isError('name') &&
                        <span className={classes.error}>
                            {getErrorMessage('name')}
                        </span>
                    }
                </Typography>
            </div>
            <div className={classes.section}>
                <Typography variant="h6" gutterBottom>
                    Election Details
                </Typography>
                <Grid container spacing={3}>
                    <Grid item xs={6}>
                        <Typography color="textSecondary" variant="subtitle2">
                            Discription:
                            {isError('description') &&
                                <span className={classes.error}>
                                   {getErrorMessage('description')}
                                </span>
                            }
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="subtitle2">
                            {getValue(election.description)}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography color="textSecondary" variant="subtitle2">
                            Singular Name:
                            {isError('button_name') &&
                                <span className={classes.error}>
                                   {getErrorMessage('button_name')}
                                </span>
                            }
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="subtitle2">
                            {getValue(election.button_name)}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography color="textSecondary" variant="subtitle2">
                            Start Date:
                            {isError('starts_at') &&
                                <span className={classes.error}>
                                   {getErrorMessage('starts_at')}
                                </span>
                            }
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="subtitle2">
                            {election.starts_at.toLocaleString(DateTime.DATETIME_FULL)}
                        </Typography>
                    </Grid>
                </Grid>
                { election.repeated ? (
                    <Grid container spacing={3}>
                        <Grid item xs={6}>
                            <Typography color="textSecondary" variant="subtitle2">
                                Election Will Cycle Every:
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                {election.repeat_amount.toString() + " " + election.repeat_period}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography color="textSecondary" variant="subtitle2">
                                Election will appear as:
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2" color="primary">
                                {electionNameWithDate}
                            </Typography>
                        </Grid>
                    </Grid>
                ) : (
                    <Alert severity="warning" className={classes.marginTop}>
                        Election will not cycle.
                    </Alert>
                )}
            </div>
            <Divider className={classes.section}/>
            <div className={classes.section}>
                <Typography variant="h6" gutterBottom>
                    Proposals
                    <Chip
                        className={classes.marginLeft}
                        label={election.proposals_active ? 'Active' : 'Admin Only'}
                        color={election.proposals_active ? 'primary' : 'default'}
                    />
                </Typography>
                {election.proposals_active &&
                    <Grid container spacing={3}>
                        <Grid item xs={6}>
                            <Typography color="textSecondary" variant="subtitle2">
                                Users who can create Proposals:
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                {getGroupTitle(election.proposal_group_id)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography color='textSecondary' variant="subtitle2">
                                Proposals Start At:
                                {isError('proposals_start_at') &&
                                    <span className={classes.error}>
                                        {getErrorMessage('proposals_start_at')}
                                    </span>
                                }
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                {getDate(election.proposals_start_at)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography color='textSecondary' variant="subtitle2">
                                Proposals End At:
                                {isError('proposals_end_at') &&
                                    <span className={classes.error}>
                                        {getErrorMessage('proposals_end_at')}
                                    </span>
                                }
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                {getDate(election.proposals_end_at)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography color='textSecondary' variant="subtitle2">
                                Number of Proposals Per User:
                                {isError('proposals_per_user') &&
                                    <span className={classes.error}>
                                        {getErrorMessage('proposals_per_user')}
                                    </span>
                                }
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                {getNumber(election.proposals_per_user)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography color='textSecondary' variant="subtitle2">
                                Send Notifications When Proposals Start?
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                {election.proposals_starting_notifications ? 'Yes' : 'No'}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography color='textSecondary' variant="subtitle2">
                                Send Warning Before Proposals End?
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                {election.proposals_ending_notifications ? 'Yes' : 'No'}
                            </Typography>
                        </Grid>
                    </Grid>
                }
                { (election.repeated && election.proposals_repeated) ? (
                    <Alert severity="info" className={classes.marginTop}>
                        Proposals will be passed on for {getNumber(election.proposals_repeat_length, 'unlimited')} cycle(s).
                    </Alert>
                ) : (
                    <Alert severity="warning" className={classes.marginTop}>
                        Proposals will not cycle.
                    </Alert>
                )}
            </div>
            <Divider className={classes.section}/>
            <div className={classes.section}>
                <Typography variant="h6" gutterBottom>
                    Recommendations
                    <Chip
                        className={classes.marginLeft}
                        label={election.recommendations_active ? 'Active' : 'Admin Only'}
                        color={election.recommendations_active ? 'primary' : 'default'}
                    />
                </Typography>
                {election.recommendations_active &&
                    <Grid container spacing={3}>
                        <Grid item xs={6}>
                            <Typography color="textSecondary" variant="subtitle2">
                                Users who can create Recommendations:
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                {getGroupTitle(election.recommendation_group_id)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography color='textSecondary' variant="subtitle2">
                                Recommendations Start At:
                                {isError('recommendations_start_at') &&
                                    <span className={classes.error}>
                                        {getErrorMessage('recommendations_start_at')}
                                    </span>
                                }
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                {getDate(election.recommendations_start_at)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography color='textSecondary' variant="subtitle2">
                                Recommendations End At:
                                {isError('recommendations_end_at') &&
                                    <span className={classes.error}>
                                        {getErrorMessage('recommendations_end_at')}
                                    </span>
                                }
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                {getDate(election.recommendations_end_at)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography color='textSecondary' variant="subtitle2">
                                Number of Recommendations Per User:
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                {getNumber(election.recommendations_per_user)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography color='textSecondary' variant="subtitle2">
                                Number of Characters Per Recommendation:
                                {isError('recommendations_character_limit') &&
                                    <span className={classes.error}>
                                        {getErrorMessage('recommendations_character_limit')}
                                    </span>
                                }
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                {election.recommendations_character_limit}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography color='textSecondary' variant="subtitle2">
                                Allow Recommnedation Templates?
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                {election.recommendations_canned_responses ? 'Yes' : 'No'}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography color='textSecondary' variant="subtitle2">
                                Recommendation Dialog Header:
                                {isError('recommendations_text') &&
                                    <span className={classes.error}>
                                        {getErrorMessage('recommendations_text')}
                                    </span>
                                }
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                {election.recommendations_text}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography color='textSecondary' variant="subtitle2">
                                Send Notifications When Recommendations Start?
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                {election.recommendations_starting_notifications ? 'Yes' : 'No'}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography color='textSecondary' variant="subtitle2">
                                Send Warning Before Recommendations End?
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                {election.recommendations_ending_notifications ? 'Yes' : 'No'}
                            </Typography>
                        </Grid>
                    </Grid>
                }
                { (election.repeated && election.recommendations_repeated) ? (
                    <Alert severity="info" className={classes.marginTop}>
                        Recommendations will be passed on for {getNumber(election.recommendations_repeat_length, 'unlimited')} cycle(s).
                    </Alert>
                ) : (
                    <Alert severity="warning" className={classes.marginTop}>
                        Recommendations will not cycle.
                    </Alert>
                )}
            </div>
            <Divider className={classes.section}/>
            <div className={classes.section}>
                <Typography variant="h6" gutterBottom>
                    Voting
                    <Chip
                        className={classes.marginLeft}
                        label={election.voting_active ? 'Active' : 'Admin Only'}
                        color={election.voting_active ? 'primary' : 'default'}
                    />
                </Typography>
                {election.voting_active ? (
                    <Grid container spacing={3}>
                        <Grid item xs={6}>
                            <Typography color="textSecondary" variant="subtitle2">
                                Users who can vote:
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                {getGroupTitle(election.voting_group_id)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography color='textSecondary' variant="subtitle2">
                                Voting Starts At:
                                {isError('voting_starts_at') &&
                                    <span className={classes.error}>
                                        {getErrorMessage('voting_starts_at')}
                                    </span>
                                }
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                {getDate(election.voting_starts_at)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography color='textSecondary' variant="subtitle2">
                                Voting Ends At:
                                {isError('voting_ends_at') &&
                                    <span className={classes.error}>
                                        {getErrorMessage('voting_ends_at')}
                                    </span>
                                }
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                {getDate(election.voting_ends_at)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography color='textSecondary' variant="subtitle2">
                                Number of Votes Per User:
                                {isError('votes_per_user') &&
                                    <span className={classes.error}>
                                        {getErrorMessage('votes_per_user')}
                                    </span>
                                }
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                {getNumber(election.votes_per_user)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography color='textSecondary' variant="subtitle2">
                                Send Notifications When Voting Begins?
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                {election.voting_starting_notifications ? 'Yes' : 'No'}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography color='textSecondary' variant="subtitle2">
                                Send Warning Before Voting Ends?
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                {election.voting_ending_notifications ? 'Yes' : 'No'}
                            </Typography>
                        </Grid>
                    </Grid>
                ) : (
                    <Alert severity="warning" className={classes.marginTop}>
                        Votes can be cast by administrators only.
                    </Alert>
                )}
            </div>
            <Divider className={classes.section}/>
            <div className={classes.section}>
                <Typography variant="h6" gutterBottom>
                    Other
                </Typography>
                <Grid container spacing={3}>
                    <Grid item xs={6}>
                        <Typography color="textSecondary" variant="subtitle2">
                            Users who can review proposals and recommendations:
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="subtitle2">
                            {getGroupTitle(election.review_group_id, 'Administrators Only')}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography color="textSecondary" variant="subtitle2">
                            Users who can view the voting results:
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="subtitle2">
                            {getGroupTitle(election.results_group_id, 'Administrators Only')}
                        </Typography>
                    </Grid>
                </Grid>
            </div>
        </div>
    )
}
